"use client";

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { likesAPI, repostsAPI, postsAPI, handleApiError } from "../../services/api";
import RepostModal from "../modals/RepostModal";
import StarRating from "../social/posts/StarRating";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { showErrorToast } from "../../utils/notificationUtils";
import "./ChannelPostReactions.css";

// Custom emoji image paths
const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";

const ChannelPostReactions = ({ post, onLike, onShare, onComment, onShowReactions, onRepost, onNewRepost }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getUserType, user: currentUser } = useAuth();
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostingPosts, setRepostingPosts] = useState(false);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);

  // Use image paths for reactions (mapped to reaction types)
  const reactions = [HappyEmoji, LoveEmoji, LaughEmoji, ShockedEmoji, SadEmoji, AngryEmoji];
  const reactionTypes = ['like', 'love', 'funny', 'wow', 'sad', 'angry'];

  const reactionTypeToEmoji = {
    'like': HappyEmoji,
    'love': LoveEmoji,
    'funny': LaughEmoji,
    'haha': LaughEmoji,
    'laugh': LaughEmoji,
    'wow': ShockedEmoji,
    'sad': SadEmoji,
    'angry': AngryEmoji
  };

  // Helper to get reaction type from emoji path
  const emojiToReactionType = {
    [HappyEmoji]: 'like',
    [LoveEmoji]: 'love',
    [LaughEmoji]: 'funny',
    [ShockedEmoji]: 'wow',
    [SadEmoji]: 'sad',
    [AngryEmoji]: 'angry'
  };

  // Helper function to render emoji (image or text)
  const renderEmoji = (emoji, size = 20) => {
    if (!emoji) return null;

    // Scale down shocked emoji slightly as it appears larger than others
    const finalSize = emoji === ShockedEmoji ? size * 0.82 : size;

    // Check if it's an image path (starts with / or contains .png)
    const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));

    if (isImagePath) {
      return (
        <img
          src={emoji}
          alt="reaction"
          onError={(e) => {
            console.error('Failed to load emoji image:', emoji);
            e.target.style.display = 'none';
          }}
          style={{
            width: `${finalSize}px`,
            height: `${finalSize}px`,
            objectFit: 'contain',
            display: 'inline-block',
            verticalAlign: 'middle',
            imageRendering: 'high-quality',
            WebkitImageRendering: 'high-quality'
          }}
        />
      );
    } else {
      // Text emoji fallback
      return <span style={{ fontSize: `${size}px`, lineHeight: 1 }}>{emoji}</span>;
    }
  };

  // Helper function to render reaction breakdown
  const renderReactionBreakdown = (reactionCounts) => {
    if (!reactionCounts || typeof reactionCounts !== 'object' || Object.keys(reactionCounts).length === 0) {
      return null;
    }

    // Filter out reactions with 0 count and sort by count (descending)
    const activeReactions = Object.entries(reactionCounts)
      .filter(([type, count]) => count > 0)
      .sort(([, a], [, b]) => b - a);

    if (activeReactions.length === 0) {
      return null;
    }

    const handleBreakdownClick = (e) => {
      e.stopPropagation();
      if (onShowReactions && post?.id) {
        onShowReactions(post.id);
      } else if (post?.id) {
        // Fallback to original navigation if onShowReactions is not provided
        navigate(`/social/posts/${post.id}`);
      }
    };

    return (
      <div
        className="channel-post-reaction-breakdown"
        onClick={handleBreakdownClick}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {activeReactions.map(([type, count], index) => {
            const emoji = reactionTypeToEmoji[type] || HappyEmoji;
            const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
            const isShocked = emoji === ShockedEmoji;
            const imgStyle = isShocked
              ? { width: "82%", height: "82%", objectFit: 'contain' }
              : { width: "100%", height: "100%", objectFit: 'contain' };

            return (
              <span
                key={type}
                className="channel-post-reaction-item"
                title={`${count} ${type} reaction${count !== 1 ? 's' : ''}`}
                style={{ zIndex: 10 - index }}
              >
                <span className="channel-post-reaction-emoji">
                  {isImagePath ? (
                    <img
                      src={emoji}
                      alt={type}
                      onError={(e) => {
                        console.error('Failed to load reaction emoji:', emoji);
                        e.target.style.display = 'none';
                      }}
                      style={imgStyle}
                    />
                  ) : (
                    <span style={{ fontSize: "14px" }}>{emoji}</span>
                  )}
                </span>
                <span className="channel-post-reaction-count">{count}</span>
              </span>
            );
          })}
        </div>
        <span className="channel-post-reaction-count" style={{ marginLeft: "8px", fontWeight: "700", color: "#4B5563" }}>
          {totalReactions}
        </span>
      </div>
    );
  };

  const getCurrentReactionEmoji = () => {
    const reactionType = post.user_reaction_type;
    if (reactionType && reactionTypeToEmoji[reactionType]) {
      return reactionTypeToEmoji[reactionType];
    }
    return null;
  };

  const currentReactionEmoji = getCurrentReactionEmoji();
  const hasReaction = !!post.user_reaction_type;
  const totalReactions = post.reactions_count || post.likes || 0;

  const clearTimeout = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Handle rating post
  const handleRatePost = async (postId, rating) => {
    // Client-side validation: Check if user is trying to rate their own post
    const postAuthorId = post?.author?.id || post?.user_id || post?.user?.id;
    const currentUserId = currentUser?.id;

    if (postAuthorId && currentUserId && postAuthorId === currentUserId) {
      const errorMessage = t(
        "postFeed.rating.cannotRateOwnPost",
        "You cannot rate your own post."
      );
      showErrorToast(errorMessage);
      throw new Error(errorMessage);
    }

    try {
      const response = await postsAPI.ratePost(postId, rating);
      const responseData = response?.data?.data || response?.data || {};

      // Update post state with backend response data using onLike (which handles post updates)
      if (onLike) {
        const updatedPost = {
          ...post,
          average_rating: responseData.average_rating ?? post.average_rating,
          user_rating: responseData.user_rating ?? rating,
          ratings_count: responseData.ratings_count ?? post.ratings_count,
        };
        onLike(updatedPost);
      }
    } catch (error) {
      console.error("Error rating post:", error);

      // Handle 403 Forbidden errors (user trying to rate own post)
      if (error?.response?.status === 403) {
        let errorMessage = t(
          "postFeed.rating.cannotRateOwnPost",
          "You cannot rate your own post."
        );

        // Check if backend sent a specific error message
        const backendMessage = error?.response?.data?.message ||
          error?.response?.data?.errors?.post_id?.[0];
        if (backendMessage) {
          errorMessage = backendMessage;
        }

        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }

      // Handle validation errors (422)
      if (error?.response?.status === 422) {
        let errorMessage = t(
          "postFeed.rating.ratingError",
          "Failed to submit rating. Please try again."
        );

        const validationErrors = error?.response?.data?.errors;
        if (validationErrors) {
          // Check for post_id validation error (from backend withValidator)
          if (validationErrors.post_id && validationErrors.post_id[0]) {
            errorMessage = validationErrors.post_id[0];
          } else if (validationErrors.rating && validationErrors.rating[0]) {
            errorMessage = validationErrors.rating[0];
          } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
        }

        showErrorToast(errorMessage);
        throw new Error(errorMessage);
      }

      // Handle other errors
      const errorMessage = error?.response?.data?.message ||
        error?.message ||
        t("postFeed.rating.ratingError", "Failed to submit rating. Please try again.");
      showErrorToast(errorMessage);
      throw error;
    }
  };

  const handleMouseEnter = () => {
    clearTimeout();
    setShowReactionPicker(true);
  };

  const handleMouseLeave = (e) => {
    // Don't close if moving to the picker
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && containerRef.current && containerRef.current.contains(relatedTarget)) {
      return;
    }
    clearTimeout();
    timeoutRef.current = window.setTimeout(() => {
      setShowReactionPicker(false);
    }, 500); // Increased delay to give more time to move to picker
  };

  useEffect(() => {
    return () => {
      clearTimeout();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the container (including the picker)
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Don't close if clicking on a reaction emoji button
        const isReactionButton = event.target.closest('.channel-post-reaction-emoji');
        if (!isReactionButton) {
          setShowReactionPicker(false);
          clearTimeout();
        }
      }
    };

    if (showReactionPicker) {
      // Use a delay to ensure reaction button clicks process first
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [showReactionPicker]);

  // Helper to get reaction type from emoji path
  const getReactionTypeFromEmoji = (emoji) => {
    return emojiToReactionType[emoji] || reactionTypes[reactions.indexOf(emoji)] || 'like';
  };

  const handleReaction = async (reaction, event) => {
    // Prevent the click from bubbling up
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Close picker immediately to prevent conflicts
    setShowReactionPicker(false);
    clearTimeout();

    try {
      // reaction is now an image path, get the reaction type from it
      const reactionType = getReactionTypeFromEmoji(reaction);
      const currentReaction = post.user_reaction_type;

      if (currentReaction === reactionType) {
        // Optimistic update: remove reaction immediately
        if (onLike) {
          const currentCounts = { ...(post.reaction_counts || {}) };
          const updatedCounts = {
            ...currentCounts,
            [reactionType]: Math.max(0, (currentCounts[reactionType] || 0) - 1)
          };

          const optimisticPost = {
            ...post,
            user_reaction_type: null,
            reaction_counts: updatedCounts,
            reactions_count: Math.max(0, (post.reactions_count || post.likes || 0) - 1),
            likes: Math.max(0, (post.likes || post.reactions?.likes || 0) - 1),
          };
          onLike(optimisticPost);
        }

        const response = await likesAPI.removeReaction(post.id);

        if (onLike) {
          let updatedPost = {
            ...post,
            user_reaction_type: null,
          };

          if (response && response.data) {
            const responseData = response.data;
            const existingReactionCounts = post.reaction_counts || {};
            const newReactionCounts = responseData.reaction_counts || {};

            const mergedReactionCounts = {
              ...existingReactionCounts,
              ...newReactionCounts
            };

            const calculatedTotal = Object.values(mergedReactionCounts).reduce((sum, count) => sum + (count || 0), 0);
            const totalReactions = responseData.reactions_count !== undefined
              ? responseData.reactions_count
              : calculatedTotal;

            const originalUpdatedAt = updatedPost.updated_at || updatedPost.created_at;

            const validReactionCounts = (
              mergedReactionCounts &&
              typeof mergedReactionCounts === 'object' &&
              !Array.isArray(mergedReactionCounts)
            ) ? mergedReactionCounts : (updatedPost.reaction_counts || {});

            updatedPost = {
              ...updatedPost,
              reactions_count: totalReactions,
              reaction_counts: validReactionCounts,
              updated_at: originalUpdatedAt,
              likes: totalReactions,
              reactions: updatedPost.reactions
                ? {
                  ...updatedPost.reactions,
                  likes: (validReactionCounts.like || 0) + (validReactionCounts.love || 0),
                  comments: updatedPost.reactions.comments || 0,
                  shares: updatedPost.reactions.shares || 0,
                }
                : {
                  likes: (validReactionCounts.like || 0) + (validReactionCounts.love || 0),
                  comments: 0,
                  shares: 0,
                },
            };
          } else {
            updatedPost.likes = Math.max(0, (post.likes || post.reactions?.likes || post.reactions_count || 0) - 1);
            updatedPost.reactions_count = updatedPost.likes;
            updatedPost.updated_at = post.updated_at || post.created_at;
          }

          onLike(updatedPost);
        }
      } else {
        let response;
        try {
          response = await likesAPI.addReaction(post.id, reactionType);
        } catch (apiError) {
          // Check if error is from notification/broadcasting (non-critical)
          const errorMessage = apiError?.response?.data?.message || apiError?.message || '';
          const errorTrace = JSON.stringify(apiError?.response?.data?.trace || []);
          const isNotificationError = errorMessage.includes('message') ||
            errorMessage.includes('Pusher') ||
            errorMessage.includes('notification') ||
            errorMessage.includes('BroadcastException') ||
            errorTrace.includes('NotificationService') ||
            errorTrace.includes('PusherBroadcaster');

          // Check if error response contains reaction data (reaction was saved despite error)
          const hasReactionData = apiError?.response?.data?.reaction ||
            apiError?.response?.data?.reaction_counts ||
            apiError?.response?.data?.reactions_count !== undefined;

          // If it's a notification/broadcasting error (even 500), the reaction was likely saved
          if (isNotificationError || (apiError?.response?.status === 500 && hasReactionData)) {
            // Use the error response data if it contains reaction info (reaction was saved)
            if (hasReactionData) {
              response = apiError.response; // Reaction was saved, use the data
            } else {
              // No data in error, do optimistic update
              response = null; // Will trigger optimistic update below
            }
            // Silently handle - don't log since reaction worked
          } else if (apiError?.response?.status === 200 || apiError?.response?.status === 201) {
            response = apiError.response; // Use the response even if there were errors
          } else {
            throw apiError; // Re-throw if it's a different error
          }
        }

        // Optimistic update: add/change reaction immediately
        if (onLike) {
          const currentCounts = { ...(post.reaction_counts || {}) };
          const oldReaction = post.user_reaction_type;
          const updatedCounts = { ...currentCounts };

          // If switching from one reaction to another:
          // 1. Decrement the old reaction count
          // 2. Increment the new reaction count
          // Total reactions count stays the same (switching, not adding)
          if (oldReaction && oldReaction !== reactionType) {
            // Switching reactions: remove old, add new
            updatedCounts[oldReaction] = Math.max(0, (updatedCounts[oldReaction] || 0) - 1);
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          } else if (!oldReaction) {
            // Adding new reaction (no previous reaction)
            updatedCounts[reactionType] = (updatedCounts[reactionType] || 0) + 1;
          }
          // If oldReaction === reactionType, we already handled that case above (removal)

          const currentTotal = post.reactions_count || post.likes || 0;
          const isSwitching = oldReaction && oldReaction !== reactionType;
          const isAdding = !oldReaction;

          const optimisticPost = {
            ...post,
            user_reaction_type: reactionType,
            reaction_counts: updatedCounts,
            reactions_count: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
            likes: isSwitching ? currentTotal : (isAdding ? currentTotal + 1 : currentTotal),
          };
          onLike(optimisticPost);
        }

        if (onLike) {
          let updatedPost = {
            ...post,
            user_reaction_type: reactionType,
          };

          if (response && response.data) {
            const responseData = response.data;
            const apiReactionType = responseData.reaction?.type || reactionType;

            // Use the backend's reaction_counts directly - it should have the correct counts
            // (old reaction removed, new reaction added)
            const backendReactionCounts = responseData.reaction_counts || {};

            // If backend doesn't provide reaction_counts, calculate from optimistic update
            const reactionCounts = Object.keys(backendReactionCounts).length > 0
              ? backendReactionCounts
              : (() => {
                const currentCounts = { ...(post.reaction_counts || {}) };
                const oldReaction = post.user_reaction_type;

                // Remove old reaction if switching
                if (oldReaction && oldReaction !== reactionType) {
                  currentCounts[oldReaction] = Math.max(0, (currentCounts[oldReaction] || 0) - 1);
                }

                // Add new reaction
                if (!oldReaction || oldReaction !== reactionType) {
                  currentCounts[reactionType] = (currentCounts[reactionType] || 0) + 1;
                }

                return currentCounts;
              })();

            const calculatedTotal = Object.values(reactionCounts).reduce((sum, count) => sum + (count || 0), 0);
            const totalReactions = responseData.reactions_count !== undefined
              ? responseData.reactions_count
              : calculatedTotal;

            const originalUpdatedAt = updatedPost.updated_at || updatedPost.created_at;
            const finalReactionType = apiReactionType;

            const validReactionCounts = (
              reactionCounts &&
              typeof reactionCounts === 'object' &&
              !Array.isArray(reactionCounts)
            ) ? reactionCounts : {};

            updatedPost = {
              ...updatedPost,
              user_reaction_type: finalReactionType,
              reactions_count: totalReactions,
              reaction_counts: validReactionCounts,
              updated_at: originalUpdatedAt,
              likes: totalReactions,
              reactions: updatedPost.reactions
                ? {
                  ...updatedPost.reactions,
                  likes: (validReactionCounts.like || 0) + (validReactionCounts.love || 0),
                  comments: updatedPost.reactions.comments || 0,
                  shares: updatedPost.reactions.shares || 0,
                }
                : {
                  likes: (validReactionCounts.like || 0) + (validReactionCounts.love || 0),
                  comments: 0,
                  shares: 0,
                },
            };
          } else {
            // Fallback: use optimistic update values
            const currentCounts = { ...(post.reaction_counts || {}) };
            const oldReaction = post.user_reaction_type;

            if (oldReaction && oldReaction !== reactionType) {
              currentCounts[oldReaction] = Math.max(0, (currentCounts[oldReaction] || 0) - 1);
            }
            if (!oldReaction || oldReaction !== reactionType) {
              currentCounts[reactionType] = (currentCounts[reactionType] || 0) + 1;
            }

            const currentTotal = post.reactions_count || post.likes || 0;
            const isSwitching = oldReaction && oldReaction !== reactionType;

            updatedPost.reaction_counts = currentCounts;
            updatedPost.reactions_count = isSwitching ? currentTotal : currentTotal + 1;
            updatedPost.likes = updatedPost.reactions_count;
            updatedPost.updated_at = post.updated_at || post.created_at;
          }

          onLike(updatedPost);
        }
      }
    } catch (error) {
      // Check if error is from notification/broadcasting (non-critical)
      const errorMessage = error?.response?.data?.message || error?.message || '';
      const errorTrace = JSON.stringify(error?.response?.data?.trace || []);
      const isNotificationError = errorMessage.includes('message') ||
        errorMessage.includes('Pusher') ||
        errorMessage.includes('notification') ||
        errorMessage.includes('BroadcastException') ||
        errorTrace.includes('NotificationService') ||
        errorTrace.includes('PusherBroadcaster');

      // If it's a notification/broadcasting error, the reaction was likely saved
      // Do optimistic update anyway - don't show error to user since reaction worked
      if (onLike) {
        const reactionType = getReactionTypeFromEmoji(reaction);
        const currentReaction = post.user_reaction_type;

        if (currentReaction === reactionType) {
          // Optimistic update for removal
          const updatedPost = {
            ...post,
            user_reaction_type: null,
            reactions_count: Math.max(0, (post.reactions_count || post.likes || 0) - 1),
            likes: Math.max(0, (post.likes || post.reactions?.likes || 0) - 1),
            reaction_counts: {
              ...post.reaction_counts,
              [reactionType]: Math.max(0, (post.reaction_counts?.[reactionType] || 0) - 1)
            }
          };
          onLike(updatedPost);
        } else {
          // Optimistic update for addition
          const oldReaction = post.user_reaction_type;
          const newCounts = { ...post.reaction_counts };
          if (oldReaction) {
            newCounts[oldReaction] = Math.max(0, (newCounts[oldReaction] || 0) - 1);
          }
          newCounts[reactionType] = (newCounts[reactionType] || 0) + 1;

          const updatedPost = {
            ...post,
            user_reaction_type: reactionType,
            reactions_count: (post.reactions_count || post.likes || 0) + (oldReaction ? 0 : 1),
            likes: (post.likes || post.reactions?.likes || 0) + (oldReaction ? 0 : 1),
            reaction_counts: newCounts
          };
          onLike(updatedPost);
        }
      }

      // Only log unexpected errors - notification errors are expected and reaction worked
      if (!isNotificationError) {
        console.error("Unexpected error during reaction:", error);
      }
      // Silently handle notification errors since reaction was saved successfully
    }
  };

  const dir = i18n.dir();

  return (
    <div className="channel-post-reactions" style={{ '--user-type-color': userTypeColor }} dir={dir}>
      <div className="channel-post-reactions-left">
        <div className="channel-post-reaction-wrapper">
          <div
            ref={containerRef}
            className="channel-post-reaction-container"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={(e) => handleMouseLeave(e)}
          >
            <button
              className={`channel-post-like-btn ${hasReaction ? "has-reaction" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                // Show reaction picker
                if (!showReactionPicker) {
                  handleMouseEnter();
                }
              }}
            >
              {currentReactionEmoji ? (
                renderEmoji(currentReactionEmoji, 36)
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              )}
            </button>

            {showReactionPicker && (
              <div
                className="channel-post-reaction-picker"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {reactions.map((reaction, index) => {
                  const reactionType = reactionTypes[index];
                  return (
                    <button
                      key={index}
                      className="channel-post-reaction-emoji"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleReaction(reaction, e);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {renderEmoji(reaction, 40)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {renderReactionBreakdown(post.reaction_counts)}
        </div>

        {onComment && (
          <button
            className="channel-post-comment-btn-integrated"
            onClick={(e) => {
              e.stopPropagation();
              onComment();
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span className="channel-post-btn-text">
              {post.comments || post.reactions?.comments || 0}{" "}
              <span className="channel-post-btn-label">
                {(post.comments || post.reactions?.comments || 0) === 1
                  ? t("channelPost.commentSingular", "comment")
                  : t("channelPost.commentPlural", "comments")}
              </span>
            </span>
          </button>
        )}

        {/* Hide repost button if this post is itself a repost or if it's sharing an adoption post */}
        {!(post.is_repost ||
          post.repost_id ||
          post.parent_post_id ||
          post.type === 'repost' ||
          (post.reposted_post && Object.keys(post.reposted_post).length > 0) ||
          (post.original_post && Object.keys(post.original_post).length > 0) ||
          post.adoption_post_id ||
          post.adoption_post?.id) && (
            <button
              className="channel-post-share-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowRepostModal(true);
              }}
              style={{
                backgroundColor: (post.user_has_reposted && (post.reposts_count || 0) > 0) ? "#F0F9FF" : undefined,
                color: (post.user_has_reposted && (post.reposts_count || 0) > 0) ? "#0EA5E9" : undefined,
              }}
            >
              <svg width="20" height="20" viewBox="300 300 600 600" fill="currentColor">
                <path d="M758.57,469.4l-227.81-.12c-58.47-.03-104.94,46.8-105.12,104.96-.05,15.04-12.77,25.99-26.48,25.69-14.76-.33-25.79-11.9-25.7-27.07.52-84.97,69.17-155.48,154.64-155.63l226.03-.41-33.52-34.12c-10.3-10.49-9.4-27,.57-36.64,10.3-9.97,26.48-10.07,37.06.5l77.5,77.48c10.01,10,11.32,25.68,1.59,36.49l-78.45,87.18c-10.04,11.16-26.41,11.4-36.99,1.9-10.38-9.31-11.76-25.62-2.02-36.6l38.7-43.62Z"/>
                <path d="M364.64,776.33c-11.06-11.06-11.42-26.42-1.33-37.64l77.13-85.73c10.26-11.4,26.48-12.63,37.66-2.59,10.73,9.64,11.49,26.09,1.43,37.35l-38.35,42.93,228.05.05c58.42.01,104.91-46.83,105.1-104.95.05-15.15,12.89-25.99,26.48-25.71,15.76.32,26.03,13.02,25.77,28.21-1.51,87.6-73,155.01-160.17,154.97l-220.51-.11,33.51,34.15c10.27,10.47,9.35,27.04-.58,36.64-10.39,10.04-26.45,10.1-37.05-.5l-77.12-77.07Z"/>
              </svg>
              <span className="channel-post-btn-text">
                {post.reposts_count || 0}{" "}
                <span className="channel-post-btn-label">
                  {t("postFeed.reposts", "reposts")}
                </span>
              </span>
            </button>
          )}

        <button className="channel-post-share-btn" onClick={onShare}>
          <svg width="20" height="20" viewBox="300 300 600 600" fill="currentColor">
            <path d="M617.2,656.78c-53.35-3.31-101.45,26.42-138.07,62.09-20.84,20.3-38.34,42.6-54.49,66.68-5.67,8.46-14.87,12.52-24.53,9.81-9.18-2.57-15.97-11.14-15.75-21.52l.1-40.88c.33-127.8,105.56-231.04,232.8-232.06l-.06-74.72c0-9.34,5.11-17.1,12.64-20.4,8.5-3.74,17.81-2.13,24.54,4.6l154.44,154.42c8.85,8.85,9.21,22.29.27,31.24l-154.42,154.42c-6.62,6.62-15.33,8.71-23.98,5.28-7.15-2.83-13.4-10.27-13.41-19.34l-.09-79.62Z"/>
          </svg>
          <span className="channel-post-btn-text">
            {post.shares || 0}{" "}
            <span className="channel-post-btn-label">
              {t("channelPostReactions.sharesLabel", "shares")}
            </span>
          </span>
        </button>
      </div>

      {/* Star Rating - positioned on the right side */}
      <div className="channel-post-reactions-right">
        <StarRating
          postId={post.id}
          averageRating={post.average_rating}
          userRating={post.user_rating}
          ratingsCount={post.ratings_count}
          onRate={handleRatePost}
        />
      </div>
      {showRepostModal && (
        <RepostModal
          isOpen={showRepostModal}
          onClose={() => {
            setShowRepostModal(false);
          }}
          post={post}
          onRepostSuccess={(repostData) => {
            // Add the new repost to the feed
            if (onNewRepost) {
              onNewRepost(repostData);
            }

            // Update original post with new repost count
            if (onRepost) {
              onRepost({
                ...post,
                reposts_count: (post.reposts_count || 0) + 1,
                user_has_reposted: true,
              });
            } else if (onLike) {
              // Fallback to onLike if onRepost not provided
              onLike({
                ...post,
                reposts_count: (post.reposts_count || 0) + 1,
                user_has_reposted: true,
              });
            }
            setShowRepostModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ChannelPostReactions;

